import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Role } from '@famora/core'
import { ApiFehler, schicke } from '../lib/api'
import { alterBerechnen } from '../lib/datum'
import { MITGLIED_PALETTE } from '../session/logic'
import { Button, Select, Sheet, Symbol, TextField, useToast } from '../ui'

export interface MitgliedBearbeiten {
  id: string
  displayName: string
  role: Role
  color: string
  birthDate?: string | null
  status: 'active' | 'invited' | 'disabled'
}

export interface MitgliedBearbeitenSheetProps {
  offen: boolean
  onSchliessen: () => void
  mitglied: MitgliedBearbeiten | null
  istSelbst?: boolean
  istOwner?: boolean
}

export function MitgliedBearbeitenSheet({
  offen,
  onSchliessen,
  mitglied,
  istSelbst = false,
  istOwner = false,
}: MitgliedBearbeitenSheetProps) {
  const qc = useQueryClient()
  const zeigeToast = useToast()

  const [name, setName] = useState(mitglied?.displayName ?? '')
  const [rolle, setRolle] = useState<Role>(mitglied?.role ?? 'guest')
  const [farbe, setFarbe] = useState<string>(mitglied?.color || MITGLIED_PALETTE[0])
  const [geburtsdatum, setGeburtsdatum] = useState(mitglied?.birthDate ?? '')
  const [status, setStatus] = useState<'active' | 'disabled'>(
    mitglied?.status === 'disabled' ? 'disabled' : 'active',
  )
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    if (mitglied) {
      setName(mitglied.displayName)
      setRolle(mitglied.role)
      setFarbe(mitglied.color || MITGLIED_PALETTE[0])
      setGeburtsdatum(mitglied.birthDate ?? '')
      setStatus(mitglied.status === 'disabled' ? 'disabled' : 'active')
      setFehler(null)
    }
  }, [mitglied, offen])

  const aendern = useMutation({
    mutationFn: async () => {
      if (!mitglied) return
      return schicke<MitgliedBearbeiten>('PATCH', `/api/members/${mitglied.id}`, {
        displayName: name.trim(),
        color: farbe,
        ...(istSelbst ? {} : { role: rolle }),
        birthDate: geburtsdatum ? geburtsdatum : null,
        ...(istSelbst ? {} : { status }),
      })
    },
    onSuccess: async (aktualisiert) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['members'] }),
        mitglied ? qc.invalidateQueries({ queryKey: ['access', mitglied.id] }) : Promise.resolve(),
        qc.invalidateQueries({ queryKey: ['me'] }),
      ])
      zeigeToast(`Profil von „${aktualisiert?.displayName ?? name}“ gespeichert`, 'erfolg')
      onSchliessen()
    },
    onError: (f: unknown) => {
      const msg = f instanceof ApiFehler ? f.message : 'Speichern fehlgeschlagen.'
      setFehler(msg)
      zeigeToast(msg, 'error')
    },
  })

  if (!mitglied) return null

  const alter = alterBerechnen(geburtsdatum)
  const darfRolleAendern = !istSelbst && (istOwner || mitglied.role !== 'owner')
  const darfStatusAendern = !istSelbst && (istOwner || mitglied.role !== 'owner')

  return (
    <Sheet
      offen={offen}
      titel={`Profil bearbeiten: ${mitglied.displayName}`}
      onSchliessen={onSchliessen}
    >
      <form
        className="stack-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim().length === 0) return
          aendern.mutate()
        }}
      >
        <TextField
          id="edit-name"
          label="Name"
          wert={name}
          onWert={setName}
          required
          autoFocus
        />

        <div className="stack-1">
          <label className="tf__label tf__label--fest">Farbe</label>
          <div className="farbwahl" role="radiogroup" aria-label="Farbe der Person">
            {MITGLIED_PALETTE.map((f, i) => {
              const gewaehlt = f.toLowerCase() === farbe.toLowerCase()
              return (
                <button
                  key={f}
                  type="button"
                  role="radio"
                  aria-checked={gewaehlt}
                  aria-label={`Farbe ${i + 1}`}
                  className={`farbwahl__punkt${gewaehlt ? ' farbwahl__punkt--aktiv' : ''}`}
                  style={{ '--person': f } as React.CSSProperties}
                  onClick={() => setFarbe(f)}
                >
                  {gewaehlt ? <Symbol name="check" className="farbwahl__haken" /> : null}
                </button>
              )
            })}
          </div>
        </div>

        <Select
          id="edit-rolle"
          label="Rolle"
          wert={rolle}
          onWert={(w) => {
            setRolle(w as Role)
          }}
          disabled={!darfRolleAendern}
          {...(!darfRolleAendern
            ? {
                hilfe: istSelbst
                  ? 'Die eigene Rolle kann man nicht selbst ändern.'
                  : 'Nur Eigentümer können die Rolle von Eigentümern ändern.',
              }
            : {})}
        >
          <option value="guest">Gast — sieht nur, was du freischaltest</option>
          <option value="child">Kind</option>
          <option value="teen">Jugendlich</option>
          <option value="adult">Erwachsen</option>
          {istOwner ? <option value="owner">Eigentümer</option> : null}
        </Select>

        <TextField
          id="edit-geburt"
          type="date"
          label="Geburtsdatum"
          wert={geburtsdatum}
          onWert={setGeburtsdatum}
          hilfe={
            alter !== null
              ? `${alter} ${alter === 1 ? 'Jahr' : 'Jahre'} alt`
              : 'Wird für altersgerechte Übersichten verwendet'
          }
        />

        <Select
          id="edit-status"
          label="Status"
          wert={status}
          onWert={(w) => {
            setStatus(w as 'active' | 'disabled')
          }}
          disabled={!darfStatusAendern}
          {...(status === 'disabled'
            ? { hilfe: 'Deaktivierte Personen können sich nicht mehr anmelden.' }
            : {})}
        >
          <option value="active">Aktiv im Haushalt</option>
          <option value="disabled">Deaktiviert / Ausgezogen</option>
        </Select>

        {fehler ? (
          <p className="fehler" role="alert">
            {fehler}
          </p>
        ) : null}

        <div className="blatt__fuss-inline">
          <Button variant="text" onClick={onSchliessen}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            laeuft={aendern.isPending}
            disabled={name.trim().length === 0}
          >
            Speichern
          </Button>
        </div>
      </form>
    </Sheet>
  )
}
